import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { captureEvent } from '../../../../services/event-capture';
import { setSignalMemberFilter } from '../../../../services/map-layer/image-layers/signal-markers';
import { AppState } from '../../../../services/store';
import { selectCurrentMemberId } from '../../../../services/store/modules/backend-service';
import {
  fetchSignal,
  getSignal,
  setSelectedSignal,
} from '../../../../services/store/modules/signal';
import GroupDetailView, {
  GroupDetailMember,
} from '../components/GroupDetailView/GroupDetailView';
import {
  formatDistanceFromMember,
  sortByOnWaterThenDistance,
} from '../helpers/signal-sorting';
import useGroup from '../hooks/use-group';
import useSignalUtilities from '../hooks/use-signal-utilities';

const SIGNAL_QUERY_PARAM = 'signal';

const GroupPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const { pathname, search } = useLocation();
  const { group, error, isLoading } = useGroup(id || '');
  const { centerMapOnSignal } = useSignalUtilities();
  const history = useHistory();
  const dispatch = useDispatch();
  const currentMemberId = useSelector(selectCurrentMemberId);

  const selectedSignalId =
    new URLSearchParams(search).get(SIGNAL_QUERY_PARAM) || undefined;

  const selectedSignal = useSelector((state: AppState) =>
    selectedSignalId ? getSignal(state, selectedSignalId) : undefined,
  );

  // Sync selection and map from URL; clear Redux when URL has no ?signal=
  useEffect(() => {
    if (!selectedSignalId) {
      dispatch(setSelectedSignal(undefined));
      return;
    }
    dispatch(setSelectedSignal(selectedSignalId));
    const run = async () => {
      try {
        await dispatch(fetchSignal(selectedSignalId));
        await centerMapOnSignal(selectedSignalId);
      } catch {
        // ignore fetch error; map may still center if signal was in store
        await centerMapOnSignal(selectedSignalId);
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSignalId]);

  // Create stable member IDs string for dependency comparison
  const groupMemberIds = group?.members?.map((member) => member.memberId) || [];
  const memberIdsKey = groupMemberIds.join(',');

  // Filter map signals to only show group members
  useEffect(() => {
    setSignalMemberFilter(groupMemberIds);

    // Clear filter when component unmounts
    return () => {
      setSignalMemberFilter(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberIdsKey]);

  const handleBack = () => {
    captureEvent({
      type: 'signal: back to groups list',
      payload: {
        groupId: id,
      },
      sendToAmplitude: true,
    });
    history.push('/maps/signal/groups');
  };

  const handleClose = () => {
    captureEvent({
      type: 'signal: close group page',
      payload: {
        groupId: id,
      },
      sendToAmplitude: true,
    });
    history.push('/maps');
  };

  const handleMemberClick = (signalId: string) => {
    if (!signalId) return;
    const isSameMember = selectedSignalId === signalId;
    if (isSameMember) {
      history.replace(pathname);
      dispatch(setSelectedSignal(undefined));
      return;
    }
    const params = new URLSearchParams(search);
    params.set(SIGNAL_QUERY_PARAM, signalId);
    history.replace(`${pathname}?${params.toString()}`);
    dispatch(setSelectedSignal(signalId));
    const run = async () => {
      try {
        await dispatch(fetchSignal(signalId));
        await centerMapOnSignal(signalId);
      } catch {
        await centerMapOnSignal(signalId);
      }
    };
    run();
  };

  // Add signal properties to top level for sorting, then transform for presentation
  const membersWithSignalData = (group?.members || []).map((member) => ({
    ...member,
    timestamp: member.signal?.timestamp,
    distance_from_member: member.signal?.distance_from_member,
  }));

  const sortedMembers = sortByOnWaterThenDistance(membersWithSignalData);

  // Move current user to top of list
  const currentUserIndex = sortedMembers.findIndex(
    (member) => member.memberId === currentMemberId,
  );
  if (currentUserIndex > 0) {
    const [currentUser] = sortedMembers.splice(currentUserIndex, 1);
    sortedMembers.unshift(currentUser);
  }

  const members: GroupDetailMember[] = sortedMembers.map(
    ({ id, memberId, username, signal, primaryWatercraftName }) => ({
      id,
      memberId,
      username,
      signalId: signal?.id,
      signalImage: signal?.image || undefined,
      signalTimestamp: signal?.timestamp,
      signalSubscribed: signal?.subscribed,
      distanceFromMember: formatDistanceFromMember(
        signal?.distance_from_member,
      ),
      isCurrentUser: memberId === currentMemberId,
      primaryWatercraftName,
    }),
  );

  return (
    <GroupDetailView
      isLoading={isLoading}
      error={!!error}
      groupName={group?.name}
      groupIconUrl={group?.iconUrl}
      memberCountLabel={`${group?.members.length} member${
        group?.members.length !== 1 ? 's' : ''
      }`}
      members={members}
      selectedSignalId={selectedSignalId}
      selectedSignal={selectedSignal ?? null}
      onBack={handleBack}
      onClose={handleClose}
      onMemberClick={handleMemberClick}
    />
  );
};

export default GroupPage;
